import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { PRIVACY_CLAUSES, LAST_UPDATED } from "@/data/policies";
import { EMAILS } from "@/constants/site";

const PrivacyPolicy = () => (
    <>
        <Seo
            title="Privacy Policy | Coaching Crafters AI"
            description="The full Data Privacy Policy of Coaching Crafters AI — what we collect, how we use it, your rights, and how to reach our privacy and grievance officers."
            path="/privacy-policy"
        />

        <section className="mx-auto max-w-4xl px-6 pt-16 sm:px-10 lg:pt-20" data-testid="privacy-hero">
            <Link
                to="/policy"
                data-testid="privacy-back-link"
                className="inline-flex items-center gap-2 text-sm font-medium text-ink/50 transition-colors hover:text-ember"
            >
                <ArrowLeft className="h-4 w-4" /> Our Policy
            </Link>
            <p className="mt-8 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-ember">
                Data Privacy Policy
            </p>
            <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl">
                <MaskedLine delay={0.1}>Your data,</MaskedLine>
                <MaskedLine delay={0.25}>
                    treated like <em className="font-medium italic text-ember">our own.</em>
                </MaskedLine>
            </h1>
            <Reveal delay={0.3}>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink/60">
                    This policy explains what Coaching Crafters AI collects, why, and the control you keep over it. It
                    applies to our website and to all four product lines — AI Web Based Software, ERP Solution,
                    Interactive Panel and Solar System. Coaching Crafters AI is the technology brand of Sarvacharya
                    Education Pvt Ltd, an education-focused company.
                </p>
                <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-ink/40" data-testid="privacy-last-updated">
                    Last updated: {LAST_UPDATED}
                </p>
            </Reveal>
        </section>

        <section className="mx-auto max-w-4xl px-6 py-14 sm:px-10" data-testid="privacy-body">
            <Reveal>
                <ol className="space-y-6">
                    {PRIVACY_CLAUSES.map((c, i) => (
                        <li key={i} className="flex gap-4 text-sm leading-relaxed text-ink/70 sm:text-base">
                            <span className="shrink-0 font-mono text-xs font-semibold text-ember">
                                {i + 1}.
                            </span>
                            <span>{c}</span>
                        </li>
                    ))}
                </ol>
            </Reveal>

            <Reveal className="mt-14">
                <div className="rounded-3xl border border-[#E2E8F4] bg-white p-8 shadow-sm" data-testid="privacy-contact-card">
                    <h2 className="font-serif text-xl font-semibold text-ink">Privacy contacts</h2>
                    <div className="mt-5 space-y-3 text-sm">
                        <a
                            href={`mailto:${EMAILS.privacy}`}
                            data-testid="privacy-email-link"
                            className="flex items-center gap-3 text-ink/75 transition-colors hover:text-ember"
                        >
                            <Mail className="h-4 w-4 text-ember" /> Privacy requests: {EMAILS.privacy}
                        </a>
                        <a
                            href={`mailto:${EMAILS.grievance}`}
                            data-testid="grievance-email-link"
                            className="flex items-center gap-3 text-ink/75 transition-colors hover:text-ember"
                        >
                            <Mail className="h-4 w-4 text-ember" /> Grievance Officer: {EMAILS.grievance}
                        </a>
                        <p className="pl-7 text-ink/50">Coaching Crafters AI, [Company Address]</p>
                    </div>
                    <p className="mt-5 text-xs leading-relaxed text-ink/45">
                        We respond to privacy requests within 30 days. This page also forms section 3 of{" "}
                        <Link to="/policy#data-privacy" className="font-semibold text-ember hover:underline">
                            Our Policy
                        </Link>
                        .
                    </p>
                </div>
            </Reveal>
        </section>
    </>
);

export default PrivacyPolicy;
